import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  X,
  Check,
  Loader2,
  Calendar,
  Briefcase,
  Wallet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVESTOR_GOALS = [
  { v: "wealth_creation", l: "Wealth creation" },
  { v: "retirement", l: "Retirement planning" },
  { v: "tax_saving", l: "Tax saving" },
  { v: "child_education", l: "Child's education" },
  { v: "review", l: "Review existing portfolio" },
  { v: "other", l: "Other" },
];

const MFD_GOALS = [
  { v: "scale_practice", l: "Scale my practice" },
  { v: "reporting", l: "Better client reporting" },
  { v: "succession", l: "Succession / continuity planning" },
  { v: "associates", l: "Manage associates / team" },
  { v: "migrate", l: "Migrate from another platform" },
  { v: "other", l: "Other" },
];

/**
 * LeadModal — shared meeting-request / get-started form for both
 * MFDs and Private Investors. Captured into `mfd_leads`; the
 * super-admin Lead funnel surfaces them with a type chip.
 */
export default function LeadModal({
  onClose,
  defaultType = null,
  source = "marketing_site",
  title = "Let's start a conversation.",
  subtitle = "Tell us a little about you. Our team will reach out personally — usually within one business day.",
}) {
  const [step, setStep] = useState(defaultType ? "form" : "type"); // type | form | done
  const [type, setType] = useState(defaultType);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    city: "",
    firm_name: "",
    arn_number: "",
    goal: "",
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) return toast.error("Please enter your name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Valid email required");
    if (!/^[+0-9 ()-]{7,}$/.test(form.mobile)) return toast.error("Valid mobile required");
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/create`, {
        lead_type: type,
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.trim(),
        city: form.city.trim(),
        firm_name: type === "mfd" ? form.firm_name.trim() : "",
        arn_number: type === "mfd" ? form.arn_number.trim() : "",
        goal: form.goal,
        preferred_time: form.preferred_time,
        message: form.message.trim(),
        source,
      });
      setStep("done");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goals = type === "mfd" ? MFD_GOALS : INVESTOR_GOALS;

  return (
    <div
      data-testid="lead-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-lg bg-[#0a1626] border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl text-white max-h-[92vh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="lead-modal-close"
          className="absolute right-4 top-4 text-white/50 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {step === "type" && (
          <TypeChooser
            onSelect={(t) => {
              setType(t);
              setStep("form");
            }}
          />
        )}

        {step === "form" && (
          <>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C8A96B] mb-3">
              <Calendar size={13} />
              {type === "mfd" ? "Partner With Us" : "Speak With Us"}
            </div>
            <h2 className="text-2xl md:text-3xl font-light tracking-[-0.02em]">
              {title}
            </h2>
            <p className="mt-2 text-sm text-white/55">{subtitle}</p>

            {!defaultType && (
              <button
                type="button"
                onClick={() => setStep("type")}
                data-testid="lead-modal-change-type"
                className="mt-4 text-[11px] text-[#C8A96B] hover:underline"
              >
                ← Change ({type === "mfd" ? "MFD" : "Investor"})
              </button>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="lead-modal-form">
              <Field label="Full name" value={form.full_name} onChange={upd("full_name")} testId="lead-name" required />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" type="email" value={form.email} onChange={upd("email")} testId="lead-email" required />
                <Field label="Mobile" value={form.mobile} onChange={upd("mobile")} testId="lead-mobile" placeholder="+91 …" required />
              </div>
              <Field label="City" value={form.city} onChange={upd("city")} testId="lead-city" />

              {type === "mfd" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Firm name" value={form.firm_name} onChange={upd("firm_name")} testId="lead-firm" />
                  <Field label="ARN number" value={form.arn_number} onChange={upd("arn_number")} testId="lead-arn" />
                </div>
              )}

              <Select
                label={type === "mfd" ? "What brings you to Kinntegra?" : "Primary goal"}
                value={form.goal}
                onChange={upd("goal")}
                testId="lead-goal"
                options={[{ v: "", l: "Select…" }, ...goals]}
              />
              <Select
                label="Preferred time"
                value={form.preferred_time}
                onChange={upd("preferred_time")}
                testId="lead-time"
                options={[
                  { v: "", l: "Any" },
                  { v: "morning", l: "Morning (9 AM – 12 PM)" },
                  { v: "afternoon", l: "Afternoon (12 – 4 PM)" },
                  { v: "evening", l: "Evening (4 – 8 PM)" },
                  { v: "weekend", l: "Weekends only" },
                ]}
              />

              <label className="block">
                <span className="text-xs text-white/55 tracking-wide">
                  Anything else? (optional)
                </span>
                <textarea
                  value={form.message}
                  onChange={upd("message")}
                  rows={3}
                  data-testid="lead-message"
                  className="mt-2 block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.06] transition resize-none"
                  placeholder={
                    type === "mfd"
                      ? "AUM size, team size, current platform, etc."
                      : "Share any specifics about your goals or current investments."
                  }
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.99] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request Consultation <ArrowUpRight size={14} />
                  </>
                )}
              </button>
              <p className="text-[11px] text-white/40 text-center">
                By submitting, you'll be contacted personally by a member
                of the Kinntegra team. No marketing, no spam.
              </p>
            </form>
          </>
        )}

        {step === "done" && (
          <div className="text-center py-6" data-testid="lead-modal-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#C8A96B]/15 grid place-items-center mb-6">
              <Check size={32} className="text-[#C8A96B]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-light tracking-[-0.02em]">
              We'll be in touch.
            </h2>
            <p className="mt-3 text-sm text-white/60 max-w-sm mx-auto leading-relaxed">
              Thank you, {form.full_name.split(" ")[0]}. Someone from
              Kinntegra will reach you on{" "}
              <span className="text-white">{form.mobile}</span> within one
              business day.
            </p>
            <button
              onClick={onClose}
              data-testid="lead-modal-success-close"
              className="mt-8 px-6 py-2.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 transition text-sm"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TypeChooser({ onSelect }) {
  return (
    <div data-testid="lead-modal-type-chooser">
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C8A96B] mb-3">
        <Calendar size={13} /> Connect With Us
      </div>
      <h2 className="text-2xl md:text-3xl font-light tracking-[-0.02em]">
        Who would you like to speak with us as?
      </h2>
      <p className="mt-3 text-sm text-white/55">
        We tailor the conversation to your context. Pick the path that
        describes you best.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        <button
          onClick={() => onSelect("mfd")}
          data-testid="lead-modal-type-mfd"
          className="text-left p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#C8A96B]/40 transition group"
        >
          <Briefcase size={22} className="text-[#C8A96B] mb-4" strokeWidth={1.4} />
          <div className="text-base font-light text-white">I'm an MFD</div>
          <div className="mt-2 text-xs text-white/55 leading-relaxed">
            I run a distribution practice and would like to explore a
            long-term partnership with Kinntegra.
          </div>
          <div className="mt-4 text-[#C8A96B] text-xs inline-flex items-center gap-1">
            Continue <ArrowUpRight size={12} />
          </div>
        </button>

        <button
          onClick={() => onSelect("investor")}
          data-testid="lead-modal-type-investor"
          className="text-left p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#C8A96B]/40 transition group"
        >
          <Wallet size={22} className="text-[#C8A96B] mb-4" strokeWidth={1.4} />
          <div className="text-base font-light text-white">I'm an Investor</div>
          <div className="mt-2 text-xs text-white/55 leading-relaxed">
            I'd like to be introduced to a Kinntegra-powered MFD
            who can manage my portfolio and goals.
          </div>
          <div className="mt-4 text-[#C8A96B] text-xs inline-flex items-center gap-1">
            Continue <ArrowUpRight size={12} />
          </div>
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder, required }) {
  return (
    <label className="block">
      <span className="text-xs text-white/55 tracking-wide">
        {label} {required && <span className="text-[#C8A96B]">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.06] transition"
      />
    </label>
  );
}

function Select({ label, value, onChange, testId, options }) {
  return (
    <label className="block">
      <span className="text-xs text-white/55 tracking-wide">{label}</span>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-2 block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C8A96B]/60 transition"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v} className="bg-[#0a1626] text-white">
            {o.l}
          </option>
        ))}
      </select>
    </label>
  );
}
